import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IRootState } from 'store';
import { IDResource, MapTemplate } from 'types';
import { logEvent } from 'services/amplitude';
import { connect, useDispatch } from 'react-redux';
import { getResources } from 'actions/digital-resources';
import Tooltip from 'components/common/tooltip/tooltip';
import {
  DigitalResourcesMapProps,
  ResourcesType,
  splitResources,
} from 'utils/helpers/resourcesMap';
import { getScholar } from 'actions/scholars';
import { hideSpinner, showSpinner } from 'actions/spinner';
import {
  defaultMapTemplate,
  minHeight,
  SVG_VIEWBOX_WIDTH,
} from 'utils/constants/map';
import useWindowSize from 'utils/hooks/useWindowSize';
import { getMapComponentByType } from 'utils/helpers/resourcesMapRenderers';
import Homeroom from 'components/common/digitalResourcesMaps/homeroom';
import styles from './index.module.scss';

const mapStateToProps = (state: IRootState) => ({
  resourcesState: state.digitalResources,
  userData: state.users.userData,
  scholars: state.scholars,
  literacyModalResult: state.appState.literacyModalResult,
});

const DigitalResourceMap: React.FC<DigitalResourcesMapProps> = ({
  resourcesState,
  userData,
  scholars,
  literacyModalResult,
}) => {
  const dispatch = useDispatch();

  const { scholar } = scholars;
  const { units } = resourcesState;

  const activeScholarId = userData?.activeScholarId;
  const noScholars =
    scholar === null &&
    userData &&
    !activeScholarId &&
    scholars.units?.length === 0;

  const [hoveredElement, setHoveredElement] = useState<
    IDResource | SVGElement | undefined
  >(undefined);
  const [hoveredElementBox, setHoveredElementBox] = useState<
    DOMRect | undefined
  >(undefined);

  useEffect(() => {
    if (activeScholarId) {
      dispatch(getResources(activeScholarId));
    }
  }, [activeScholarId, dispatch]);

  useEffect(() => {
    if (activeScholarId) {
      dispatch(getScholar(activeScholarId));
    }
  }, [activeScholarId, dispatch]);

  useEffect(() => {
    if (!scholar && !noScholars) {
      dispatch(showSpinner());
    } else {
      dispatch(hideSpinner());
    }

    if (noScholars) {
      dispatch(hideSpinner());
    }
  }, [dispatch, noScholars, scholar, scholars]);

  const onHoveredChanged = useCallback(
    (resource: IDResource | undefined, element: SVGElement | undefined) => {
      setHoveredElement(resource);
      const box = element?.getBoundingClientRect();
      setHoveredElementBox(box);
    },
    []
  );

  const resources = activeScholarId ? units[activeScholarId] : [];

  const mapTemplate = scholar?.settings?.mapTemplate || defaultMapTemplate;
  const logClickOnCategory = () => {
    logEvent('HomeroomLinkClick');
  };

  const ws = useWindowSize();
  const height = useMemo(() => {
    const svgHeightRate = SVG_VIEWBOX_WIDTH / window.innerWidth;
    const h = (ws.height || window.innerHeight) * svgHeightRate;
    return Math.ceil(!!ws.height && h > minHeight ? h : minHeight);
  }, [ws.height]);

  const split = splitResources(resources);

  const getLinksElements = () => {
    const MathLink = getMapComponentByType(ResourcesType.Math, mapTemplate);
    const MiscellaneousLink = getMapComponentByType(
      ResourcesType.Miscellaneous,
      mapTemplate
    );
    const LanguageArtsLink = getMapComponentByType(
      ResourcesType.LanguageArts,
      mapTemplate
    );
    const ArtLink = getMapComponentByType(ResourcesType.Art, mapTemplate);
    const SocialStudiesLink = getMapComponentByType(
      ResourcesType.Social,
      mapTemplate
    );
    const ScienceLink = getMapComponentByType(
      ResourcesType.Science,
      mapTemplate
    );
    const TechnologySkillsLink = getMapComponentByType(
      ResourcesType.Tech,
      mapTemplate
    );

    const ml = (
      <MathLink
        resources={split.math}
        onHoveredElementChanged={onHoveredChanged}
        onClick={logClickOnCategory}
      />
    );
    const lal = (
      <LanguageArtsLink
        resources={split['language arts']}
        onHoveredElementChanged={onHoveredChanged}
        onClick={logClickOnCategory}
      />
    );
    const sl = (
      <ScienceLink
        resources={split.science}
        onHoveredElementChanged={onHoveredChanged}
        onClick={logClickOnCategory}
      />
    );
    const al = (
      <ArtLink
        resources={split.art}
        onHoveredElementChanged={onHoveredChanged}
        onClick={logClickOnCategory}
      />
    );
    const mil = (
      <MiscellaneousLink
        resources={split.miscellaneous}
        onHoveredElementChanged={onHoveredChanged}
        onClick={logClickOnCategory}
      />
    );
    const ssl = (
      <SocialStudiesLink
        resources={split.social}
        onHoveredElementChanged={onHoveredChanged}
        onClick={logClickOnCategory}
      />
    );
    const tsl = (
      <TechnologySkillsLink
        resources={split['technology skills']}
        onHoveredElementChanged={onHoveredChanged}
        onClick={logClickOnCategory}
      />
    );

    const sortDRLinks = (): JSX.Element => {
      const littlePigsSortedLinks = (
        <>
          {ml} {lal} {sl} {al} {mil} {ssl} {tsl}
        </>
      );
      const fantasyLandSortedLinks = (
        <>
          {ml} {al} {lal} {sl} {tsl} {ssl} {mil}
        </>
      );
      const underseaSortedLinks = (
        <>
          {ml} {mil} {lal} {al} {ssl} {sl} {tsl}
        </>
      );
      const futureCitySortedLinks = (
        <>
          {tsl} {lal} {ml} {mil} {sl} {al} {ssl}
        </>
      );

      switch (mapTemplate) {
        case MapTemplate.littlePigs:
          return littlePigsSortedLinks;

        case MapTemplate.fantasyLand:
          return fantasyLandSortedLinks;

        case MapTemplate.undersea:
          return underseaSortedLinks;

        case MapTemplate.futureCity:
          return futureCitySortedLinks;

        default:
          return littlePigsSortedLinks;
      }
    };

    const links = sortDRLinks();

    return links;
  };

  return (
    <div
      className={`${styles.page} ${
        (scholar || noScholars) && styles[mapTemplate]
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        x="0"
        y="0"
        enableBackground={`0 0 ${SVG_VIEWBOX_WIDTH} ${height}`}
        viewBox={`0 0 ${SVG_VIEWBOX_WIDTH} ${height}`}
      >
        {(scholar || noScholars) && (
          <Homeroom
            dRMapLinkElements={getLinksElements()}
            literacyModalResult={literacyModalResult}
            scholar={scholar}
          />
        )}
      </svg>
      <Tooltip hoveredElement={hoveredElement} elementBox={hoveredElementBox} />
    </div>
  );
};

export default connect(mapStateToProps)(DigitalResourceMap);
